import React, { useState } from "react";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import InputBox from "../../Components/InputBox";
import CustomButton from "../../Components/Button/CustomButton";
import { Link, useNavigate } from "react-router-dom";
import { UserAuthApi } from "../../api/requests/userAuth/userAuthApi";
import { handleAlert } from "../../utils/handleAlert";
import { Colors } from "../../Theme/Theme";
import LockIcon from "@mui/icons-material/Lock";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SecurityIcon from "@mui/icons-material/Security";
import FiLogo from "../../Components/FiLogo";

const SignIn = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.down("md"));

  const handleSignIn = async () => {
    let hasError = false;
    if (!email) { setEmailError(true); hasError = true; } else setEmailError(false);
    if (!password) { setPasswordError(true); hasError = true; } else setPasswordError(false);
    if (!hasError) {
      try {
        setLoading(true);
        const data = await UserAuthApi.login(email, password);
        if (data?.token) {
          localStorage.setItem("Token", data.token);
          localStorage.setItem("Role", data?.role);
          localStorage.setItem("user_details", JSON.stringify(data?.user));
          handleAlert(data?.msg, "success");
          navigate("/dashboard");
        } else {
          setLoading(false);
          handleAlert(data?.msg === "please signUp first" ? "This email hasn't been registered" : data?.msg, "error");
        }
      } catch (err) { setLoading(false); handleAlert(err?.message, "error"); }
    } else handleAlert("Please fill all fields", "warning");
    setLoading(false);
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {!isMd && (
        <Box sx={{
          flex: 1, backgroundColor: Colors.bgColor,
          display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center",
          px: 6, position: "relative", overflow: "hidden",
          borderRight: `1px solid ${Colors.dividerColor}`,
        }}>
          <Box sx={{ position: "relative", zIndex: 2, textAlign: "center", maxWidth: 360 }}>
            <Box sx={{ mb: 5 }}>
              <FiLogo size="lg" />
            </Box>
            <Typography variant="h4" sx={{ color: Colors.primaryTextColor, fontWeight: 700, mb: 2, lineHeight: 1.2, fontSize: "28px" }}>
              Welcome Back
            </Typography>
            <Typography sx={{ color: Colors.secondaryColor, mb: 5, lineHeight: 1.7, fontSize: "15px" }}>
              Access your dashboard, manage transactions, and grow your business with India's leading fintech aggregator.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, alignItems: "flex-start", mx: "auto", width: "fit-content" }}>
              {[
                { icon: <LockIcon sx={{ fontSize: 18 }} />, text: "Bank-grade security" },
                { icon: <TrendingUpIcon sx={{ fontSize: 18 }} />, text: "Real-time analytics" },
                { icon: <SecurityIcon sx={{ fontSize: 18 }} />, text: "PCI DSS compliant" },
              ].map((item, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box sx={{
                    width: 36, height: 36, borderRadius: "8px",
                    backgroundColor: Colors.blueSoft, border: `1px solid ${Colors.dividerColor}`,
                    display: "flex", alignItems: "center", justifyContent: "center", color: Colors.primary,
                  }}>{item.icon}</Box>
                  <Typography sx={{ color: Colors.secondaryColor, fontSize: "13px", fontWeight: 500 }}>{item.text}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      <Box sx={{
        flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
        backgroundColor: Colors.white, px: { xs: 3, md: 6 }, py: 4,
      }}>
        <Box sx={{ width: "100%", maxWidth: 380 }}>
          {isMd && <Box sx={{ mb: 4 }}><FiLogo size="sm" /></Box>}
          <Typography sx={{ fontWeight: 700, color: Colors.primaryTextColor, mb: 0.5, fontSize: "24px" }}>Sign in</Typography>
          <Typography sx={{ color: Colors.secondaryColor, mb: 4, fontSize: "14px" }}>
            New user?{" "}
            <Link to="/signup" style={{ color: Colors.primary, fontWeight: 600, textDecoration: "none" }}>Create an account</Link>
          </Typography>

          <InputBox label="Mobile Number" placeholder="Enter your mobile number" type="text" value={email} required onChange={(e) => { setEmail(e.target.value); if (e.target.value) setEmailError(false); }} error={emailError} helperText={emailError && "Mobile number is required"} />
          <InputBox label="Password" placeholder="Enter your password" type="password" value={password} required onChange={(e) => { setPassword(e.target.value); if (e.target.value) setPasswordError(false); }} showPasswordToggle error={passwordError} helperText={passwordError && "Password is required"} onKeyDown={(e) => e.key === "Enter" && handleSignIn()} />

          <CustomButton
            onClick={handleSignIn} disabled={loading} borderRadius="10px"
            customStyles={{
              backgroundColor: Colors.primary, color: Colors.white,
              fontWeight: 600, mt: 1, py: 1.5, fontSize: "14px",
              boxShadow: "0 1px 3px rgba(37,99,235,0.2)",
              "&:hover": { backgroundColor: Colors.accentHover },
            }}
          >
            {loading ? "Signing in..." : "Sign In"}
          </CustomButton>
        </Box>
      </Box>
    </Box>
  );
};

export default SignIn;
